import {
  EuiCallOut,
  EuiMarkdownFormat,
  EuiPanel,
  EuiSkeletonText,
  EuiTabbedContent,
  EuiText,
} from "@inscopix/ideas-eui";
import { ToolSpecViewer } from "../ToolSpecViewer/ToolSpecViewer";
import { ToolVersion } from "./ToolPanelVersions";
import { useQuery } from "@tanstack/react-query";
import { getEnvVar } from "ideas.env";
import { getRequestHeaders } from "utils/getRequestHeaders";
import axios from "axios";
import { Tag } from "components/ToolBrowser/Tag";
import moment from "moment";
import { filesize } from "utils/filesize";

interface ToolPanelVersionDetailProps {
  toolVersion: ToolVersion;
}

type ContainerImageDetails = {
  id: number;
  name: string;
  label: string;
  date_created: string;
  date_updated: string;
  tags: string[] | null;
  description: string | null;
  readme: string | null;
  size: number;
  user: string | null;
  tenant: number;
};

const useGetContainerImageQuery = (containerId: number) => {
  return useQuery<ContainerImageDetails, Error>({
    queryFn: async () => {
      const url = getEnvVar("URL_TES_CONTAINER_DETAILS")(containerId);
      const headers = await getRequestHeaders();
      const res = await axios.get<ContainerImageDetails>(url, { headers });
      return res.data;
    },
  });
};

const ContainerImageDetail = ({ version }: { version: ToolVersion }) => {
  const { data, isLoading, error } = useGetContainerImageQuery(
    version.container_image,
  );

  if (isLoading) {
    return <EuiSkeletonText lines={10} />;
  }

  if (error) {
    return (
      <EuiCallOut
        title={"Error loading container information"}
        color="danger"
      />
    );
  }

  if (version.container_image === null) {
    return <div>No container image specified.</div>;
  }

  return (
    <EuiText size="s">
      <dl>
        <dt>Name:</dt>
        <dd>{data?.name}</dd>

        <dt>Description:</dt>
        <dd>
          {data.description === "" || data.description === null
            ? "None"
            : data.description}
        </dd>

        <dt>Created:</dt>
        <dd>{moment(data.date_created).fromNow()}</dd>

        <dt>Updated:</dt>
        <dd>{moment(data.date_updated).fromNow()}</dd>

        <dt>Storage:</dt>
        <dd>{filesize(data.size)}</dd>

        <dt>Tags:</dt>
        <dd>
          {data.tags
            ? data.tags.map((tag) => <Tag key={tag} tag={tag} />)
            : "None"}
        </dd>

        <dt>Overview:</dt>
        <dd>
          {data.readme !== null && data.readme !== "" ? (
            <EuiPanel hasBorder>
              <EuiMarkdownFormat>{data.readme ?? ""}</EuiMarkdownFormat>
            </EuiPanel>
          ) : (
            "None"
          )}
        </dd>
      </dl>
    </EuiText>
  );
};

export const ToolPanelVersionDetail = ({
  toolVersion,
}: ToolPanelVersionDetailProps) => {
  const tabs = [
    {
      id: "specification",
      name: "Specification",
      content: <ToolSpecViewer version={toolVersion} />,
    },
  ];
  if (toolVersion.changelog !== null && toolVersion.changelog.trim() !== "") {
    tabs.push({
      id: "changelog",
      name: "Changelog",
      content: (
        <EuiMarkdownFormat textSize="s">
          {toolVersion.changelog}
        </EuiMarkdownFormat>
      ),
    });
  }
  if (toolVersion.container_image !== null) {
    tabs.push({
      id: "container-image",
      name: "Container Image",
      content: <ContainerImageDetail version={toolVersion} />,
    });
  }
  return (
    <EuiTabbedContent
      tabs={tabs}
      initialSelectedTab={tabs[0]}
      autoFocus="selected"
    />
  );
};
